




































































































































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import TableList from "@/components/partials/TableList.vue";
import TDSButton from "@/components/common/TDSButton.vue";
import TDSSelect from "@/components/common/TDSSelect.vue";
import TDSModal from "@/components/partials/TDSModal/TDSModal.vue";
import HistoryItemCardContent from "@/components/partials/Account/HistoryItemCardContent.vue";
import { TransactionHistoryItem } from "@/interfaces/entities/TransactionHistoryItem";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import { UserCreditHistoryRequest } from "@/interfaces/httpRequests/UserCreditHistoryRequest";
import { PaginationResponse } from "@/interfaces/httpResponses/UserCreditHistoryResponse";

@Component({
    components: { TDSSpinner, HistoryItemCardContent, TDSButton, TDSSelect, TDSModal, TableList, DefaultLayout }
})
export default class AccountHistory extends Vue {
    @Prop({ type: String, default: "" }) itemId!: string;

    get displayTimeZone(): string {
        return this.$store.state.userTimeZone;
    }

    get tableColumnsHistory(): object {
        return {
        date: {
            label: this.$t("account.history.dateColumnLabel", { timezone: this.displayTimeZone }) as string,
            width: "160px",
            sortable: true
        },
            type: {
                label: this.$t("tableHeader.type") as string,
                sortable: false
            },
            description: {
                label: this.$t("tableHeader.description") as string,
                sortable: false
            },
            amount: {
            label: this.$t("tableHeader.amount") as string,
                width: "140px",
            sortable: true,
                align: "right"
        },
            actions: {
                label: "",
                width: "80px",
                sortable: false,
                align: "right"
            }
    };
    }

    private transactionHistory: Array<TransactionHistoryItem> = [];
    private isLoading: boolean = false;
    private selectedMonth: { value: string; label: string } | null = null;
    private monthOptions: Array<{ value: string; label: string }> = [];
    private currentPage: number = 1;
    private itemsPerPage: number = 100;
    private pagination: PaginationResponse | null = null;
    private sortBy: "date" | "amount" = "date";
    private sortDir: "ASC" | "DESC" = "DESC";
    private showDownloadDropdown: boolean = false;
    private isDownloading: boolean = false;
    private selectedTransaction: TransactionHistoryItem | null = null;
    private showTransactionModal: boolean = false;
    private customDateRange: { startDate: Date | null; endDate: Date | null } = { startDate: null, endDate: null };
    private leftCalendarMonth: Date = new Date();
    private rightCalendarMonth: Date = new Date(new Date().setMonth(new Date().getMonth() + 1));
    private showCustomDatePicker: boolean = false;

    get isMobile(): boolean {
        return this.$store.state.isMobile;
    }

    private fetchInProgress: boolean = false;
    private isInitialized: boolean = false;

    async created() {
        document.title = process.env.VUE_APP_THEME_TITLE + " | " + this.$t("account.history.pageTitle");
        // Ensure account details are loaded before initializing months
        if (!this.$store.state.account.createdDate) {
            await this.$store.dispatch("FETCH_ACCOUNT_DETAILS");
        }
        this.initializeMonthOptions();
        this.initializeSorting();
        this.initializeCustomCalendars();
        // Delay initial fetch to ensure component is fully initialized
        this.$nextTick(() => {
            this.isInitialized = true;
            this.fetchData();
            // Set up click listener for Custom option
            this.setupCustomOptionClickListener();
        });
        // Close dropdown when clicking outside
        document.addEventListener("click", this.handleClickOutside);
    }

    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
        // Note: We don't remove the click listener from setupCustomOptionClickListener
        // because it's using event delegation and checking for our component's elements
        // It will naturally not fire for other components
    }

    private handleClickOutside(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const wrapper = this.$el?.querySelector(".download-button-wrapper");
        if (wrapper && !wrapper.contains(target)) {
            this.showDownloadDropdown = false;
        }
    }

    /**
     * Show the custom date picker.
     * This method is ONLY called when the user explicitly clicks the "Custom" option.
     */
    private showCustomDatePickerMethod() {
        this.showCustomDatePicker = true;
    }

    /**
     * Set up a click listener specifically for the "Custom" option in the month select dropdown.
     * This listener ONLY handles showing/hiding the date picker when Custom is clicked.
     */
    private setupCustomOptionClickListener() {
        const clickHandler = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            
            // Check if click is on a list item
            const listItem = target.closest("li.list-group-item");
            if (!listItem) {
                return;
            }
            
            // Check if this list item is inside the month-select dropdown
            const monthSelect = this.$el?.querySelector(".month-select");
            if (!monthSelect || !monthSelect.contains(listItem)) {
                return;
            }
            
            // Get all list items in the dropdown (excluding headers)
            const allListItems = monthSelect.querySelectorAll("li.list-group-item:not(.is-header)");
            const clickedIndex = Array.from(allListItems).indexOf(listItem);
            
            // "Custom" is always the first option (index 0) in monthOptions
            // Check if the clicked item is the first item in the dropdown
            if (clickedIndex === 0) {
                // Verify it's actually the "custom" option by checking monthOptions
                const customOption = this.monthOptions[0];
                if (customOption && customOption.value === "custom") {
                    // This is the Custom option - show the date picker
                    this.showCustomDatePickerMethod();
                }
            }
        };
        
        // Store the handler so we can remove it later if needed
        (this as any).customOptionClickHandler = clickHandler;
        document.addEventListener("click", clickHandler);
    }

    @Watch("$route.query", { immediate: false, deep: false })
    onRouteQueryChange(newQuery: any, oldQuery: any) {
        // Don't react if component isn't initialized yet
        if (!this.isInitialized || this.fetchInProgress) {
            return;
        }

        // Ignore initial watch trigger (when oldQuery is undefined)
        if (oldQuery === undefined) {
            return;
        }

        // Only react to actual sort changes, not all query changes
        const sortKeyChanged = newQuery?.sortKey !== oldQuery?.sortKey;
        const sortDirChanged = newQuery?.sortDir !== oldQuery?.sortDir;

        // Only fetch if sort actually changed
        if (sortKeyChanged || sortDirChanged) {
            this.initializeSorting();
            this.currentPage = 1;
            this.fetchData();
        }
    }

    private initializeSorting() {
        const sortKey = this.$route.query.sortKey as string;
        const sortDir = this.$route.query.sortDir as string;

        if (sortKey === "date" || sortKey === "amount") {
            this.sortBy = sortKey;
        } else {
            // Default to date if no sort key in route
            this.sortBy = "date";
        }

        if (sortDir === "ASC" || sortDir === "DESC") {
            this.sortDir = sortDir;
        } else {
            // Default to DESC if no sort dir in route
            this.sortDir = "DESC";
        }
    }

    private initializeMonthOptions() {
        const months: Array<{ value: string; label: string }> = [];
        
        // Add "Custom" option at the beginning with dynamic label
        months.push({ value: "custom", label: this.getCustomDateRangeLabel() });
        
        const now = new Date();
        const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        // Get account creation date from store
        const accountCreatedDate = this.$store.state.account?.createdDate;
        let startDate: Date;

        if (accountCreatedDate) {
            const createdDate = new Date(accountCreatedDate);
            // Start from the first day of the creation month
            startDate = new Date(createdDate.getFullYear(), createdDate.getMonth(), 1);
        } else {
            // Fallback: go back 12 months if account date is not available
            startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        }

        // Generate months from current month back to account creation date
        let currentDate = new Date(currentMonth);
        let monthIndex = 0;

        while (currentDate >= startDate) {
            const monthValue = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
            let monthLabel = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });

            // Add "(Current)" to the current month
            if (monthIndex === 0) {
                monthLabel = monthLabel + " (Current)";
            }

            months.push({ value: monthValue, label: monthLabel });

            // Move to previous month
            currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
            monthIndex++;
        }

        this.monthOptions = months;
        // Set current month (second in the list, after Custom) as default
        this.selectedMonth = months.length > 1 ? months[1] : null;
    }

    private getMonthDateRange(monthValue: string): { startDate: string; endDate: string } {
        const [year, month] = monthValue.split("-").map(Number);
        // Start of month: first day at 00:00:00
        const startDate = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
        // End of month: last day at 23:59:59
        const endDate = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));

        return {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
        };
    }

    private async fetchData() {
        // Prevent multiple simultaneous calls
        if (this.isLoading) {
            return;
        }
        if (!this.selectedMonth || this.fetchInProgress) {
            return;
        }

        // If custom is selected but range is not valid, don't fetch
        if (this.selectedMonth.value === "custom" && !this.isCustomRangeValid) {
            return;
        }

        this.isLoading = true;
        this.fetchInProgress = true;

        try {
            let startDate: string;
            let endDate: string;

            if (this.selectedMonth.value === "custom" && this.customDateRange.startDate && this.customDateRange.endDate) {
                // Use custom date range (dates are already in UTC)
                startDate = this.customDateRange.startDate.toISOString();
                endDate = this.customDateRange.endDate.toISOString();
            } else {
                // Use month range
                const range = this.getMonthDateRange(this.selectedMonth.value);
                startDate = range.startDate;
                endDate = range.endDate;
            }

            const request: UserCreditHistoryRequest = {
                startDate,
                endDate,
                pagination: {
                    page: this.currentPage,
                    pageSize: this.itemsPerPage,
                    sortBy: this.sortBy,
                    sortDir: this.sortDir
                }
            };

            const response = await this.$store.dispatch("FETCH_USER_CREDIT_HISTORY", request);

            this.transactionHistory = response.data.items;
            this.pagination = response.data.pagination;
        } catch (e) {
            const error = e as any;
            console.error("[History] fetchData() FAILED:", error);
            // Don't show error toast on 404 to prevent spam
            if (error?.response?.status !== 404) {
                this.$toastr.error("Failed to load transaction history. Please try again later.");
            }
            // Set empty data to prevent retries
            this.transactionHistory = [];
            this.pagination = null;
        } finally {
            this.isLoading = false;
            this.fetchInProgress = false;
        }
    }

    private onMonthChange(month: { value: string; label: string } | null) {
        // Prevent unselecting - if null, keep current selection
        if (!month) {
            if (this.selectedMonth) {
                return;
            }
            if (this.monthOptions.length > 1) {
                this.selectedMonth = this.monthOptions[1]; // Skip "Custom" option
                return;
            }
            return;
        }

        // If the VALUE hasn't changed (only label changed), just update the label
        if (this.selectedMonth && month.value === this.selectedMonth.value) {
            this.selectedMonth = { ...this.selectedMonth, label: month.label };
            return;
        }

        const isCustom = month.value === "custom";
        const wasCustom = this.selectedMonth?.value === "custom";
        
        this.selectedMonth = month;
        
        // If custom is selected, handle custom logic (but DON'T show picker - that's ONLY handled by click listener)
        if (isCustom) {
            // If we have a valid range, fetch data
            if (this.isCustomRangeValid) {
                this.currentPage = 1;
                this.fetchData();
                return;
            }
            // If no valid range, initialize calendars
            if (!this.customDateRange.startDate && !this.customDateRange.endDate) {
                this.customDateRange = { startDate: null, endDate: null };
                this.initializeCustomCalendars();
            }
            // Clear any existing transactions when switching to custom without a range
            this.transactionHistory = [];
            this.pagination = null;
            return;
        }
        
        // Clear custom date range and hide picker when switching away from custom
        this.customDateRange = { startDate: null, endDate: null };
        this.showCustomDatePicker = false;
        
        // Reset Custom option label back to "Custom" if we were on custom
        if (wasCustom) {
            this.resetCustomOptionLabel();
        }
        
        this.currentPage = 1;
        this.fetchData();
    }

    private toggleDownloadDropdown() {
        this.showDownloadDropdown = !this.showDownloadDropdown;
    }

    private async downloadSelectedPeriod() {
        this.showDownloadDropdown = false;
        if (!this.selectedMonth) return;

        this.isDownloading = true;
        try {
            let startDate: string;
            let endDate: string;

            // Check if custom date range is selected
            if (this.selectedMonth.value === "custom" && this.customDateRange.startDate && this.customDateRange.endDate) {
                // Use custom date range (dates are already in UTC)
                startDate = this.customDateRange.startDate.toISOString();
                endDate = this.customDateRange.endDate.toISOString();
            } else {
                // Use month range
                const range = this.getMonthDateRange(this.selectedMonth.value);
                startDate = range.startDate;
                endDate = range.endDate;
            }

            await this.$store.dispatch("DOWNLOAD_USER_CREDIT_HISTORY_CSV", {
                from: startDate,
                to: endDate
            });
        } catch (e) {
            console.error("[History] Failed to download CSV", e);
            this.$toastr.error("Failed to download transaction history. Please try again later.");
        } finally {
            this.isDownloading = false;
        }
    }

    private async downloadAllTime() {
        this.showDownloadDropdown = false;

        this.isDownloading = true;
        try {
            // Don't send from/to params to use API defaults (1 month ago to now)
            await this.$store.dispatch("DOWNLOAD_USER_CREDIT_HISTORY_CSV", {from: new Date("1970-01-01").toISOString()});
        } catch (e) {
            console.error("[History] Failed to download CSV", e);
            this.$toastr.error("Failed to download transaction history. Please try again later.");
        } finally {
            this.isDownloading = false;
        }
    }

    private openTransactionDetails(transaction: TransactionHistoryItem) {
        this.selectedTransaction = transaction;
        this.showTransactionModal = true;
    }

    private closeTransactionDetails() {
        this.showTransactionModal = false;
        this.selectedTransaction = null;
    }

    private shouldShowReceiptButton(transaction: TransactionHistoryItem): boolean {
        const type = transaction.type?.toLowerCase() || "";
        return type === "credit_top_up" || type === "purchase" || type.includes("credit top-up") || type.includes("purchase");
    }

    private async downloadReceipt(transaction: TransactionHistoryItem) {
        try {
            // Use the correct receipt endpoint: /payments/receipts/{txnId}
            const receiptUrl = `/payments/receipts/${transaction.transactionId}`;
            await this.$store.dispatch("FETCH_RECEIPT", receiptUrl);
        } catch (e) {
            console.error("[History] Failed to download receipt", e);
            this.$toastr.error("Failed to download receipt. Please try again later.");
        }
    }

    get paginatedTransactions(): Array<TransactionHistoryItem> {
        return this.transactionHistory;
    }

    get totalTransactions(): number {
        return this.pagination?.totalItems || 0;
    }

    get totalPages(): number {
        return this.pagination?.totalPages || 0;
    }

    get paginationStart(): number {
        if (!this.pagination || this.totalTransactions === 0) return 0;
        return (this.currentPage - 1) * this.itemsPerPage + 1;
    }

    get paginationEnd(): number {
        if (!this.pagination) return 0;
        return Math.min(this.currentPage * this.itemsPerPage, this.totalTransactions);
    }

    get visiblePages(): number[] {
        const pages: number[] = [];
        const maxVisible = 3;
        let start = Math.max(1, this.currentPage - 1);
        const end = Math.min(this.totalPages, start + maxVisible - 1);
        start = Math.max(1, end - maxVisible + 1);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    }

    private goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
            this.currentPage = page;
            this.fetchData();
        }
    }


    private getTransactionType(item: TransactionHistoryItem): string {
        const type = item.type || "";

        // Check if it's SCREAMING_SNAKE_CASE (all uppercase with underscores)
        if (type && /^[A-Z_]+$/.test(type) && type.includes("_")) {
            // Convert SCREAMING_SNAKE_CASE to Title Case
            return type
                .split("_")
                .map((word: string) => word.charAt(0) + word.slice(1).toLowerCase())
                .join(" ");
        }

        // Return other formats as-is
        return type;
    }

    private getTransactionDescription(item: TransactionHistoryItem): string {
        return item.description || "";
    }

    private getTransactionAmount(item: TransactionHistoryItem): { value: number; currency: string } {
        const amount = item.purchasePrice || 0;
        const currency = item.currency || "USD";
        return { value: amount, currency };
    }

    private handleSort(column: "date" | "amount") {
        if (this.fetchInProgress) {
            return;
        }

        if (this.sortBy === column) {
            // Toggle direction if same column
            this.sortDir = this.sortDir === "ASC" ? "DESC" : "ASC";
        } else {
            // New column, default to DESC
            this.sortBy = column;
            this.sortDir = "DESC";
        }

        this.currentPage = 1;
        this.fetchData();
    }

    // Custom date range picker methods
    private initializeCustomCalendars() {
        const now = new Date();
        this.leftCalendarMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        this.rightCalendarMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    }

    private get weekDays(): string[] {
        const weekDays = this.$t("general.weekDayShort");
        if (Array.isArray(weekDays)) {
            return weekDays as string[];
        }
        // Fallback if translation returns object
        return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    }

    private get monthNames(): string[] {
        return this.$t("general.monthShort") as unknown as string[]; // first to unknown then to string[] to avoid type errors
    }

    private formatCalendarMonth(date: Date): string {
        return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    }

    private navigateCalendar(side: "left" | "right", direction: number) {
        if (side === "left") {
            this.leftCalendarMonth = new Date(this.leftCalendarMonth.getFullYear(), this.leftCalendarMonth.getMonth() + direction, 1);
            // Keep right calendar one month ahead
            this.rightCalendarMonth = new Date(this.leftCalendarMonth.getFullYear(), this.leftCalendarMonth.getMonth() + 1, 1);
        } else {
            this.rightCalendarMonth = new Date(this.rightCalendarMonth.getFullYear(), this.rightCalendarMonth.getMonth() + direction, 1);
            // Keep left calendar one month behind
            this.leftCalendarMonth = new Date(this.rightCalendarMonth.getFullYear(), this.rightCalendarMonth.getMonth() - 1, 1);
        }
    }

    private getCalendarDays(month: Date): Array<{ date: Date; isOutOfMonth: boolean; isFuture: boolean }> {
        const days: Array<{ date: Date; isOutOfMonth: boolean; isFuture: boolean }> = [];
        const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday
        
        const now = new Date();
        now.setHours(23, 59, 59, 999); // End of today
        
        for (let i = 0; i < 42; i++) { // 6 weeks * 7 days
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            
            const isOutOfMonth = currentDate.getMonth() !== month.getMonth();
            const isFuture = currentDate.getTime() > now.getTime();
            
            days.push({
                date: new Date(currentDate),
                isOutOfMonth,
                isFuture
            });
        }
        
        return days;
    }

    private selectDate(date: Date, _side: "left" | "right") {
        // Don't allow selecting future dates (check in UTC)
        const now = new Date();
        const nowUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        const dateUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
        if (dateUTC > nowUTC) {
            return;
        }
        
        const clickedTime = dateUTC;
        const startTime = this.customDateRange.startDate ? Date.UTC(
            this.customDateRange.startDate.getUTCFullYear(),
            this.customDateRange.startDate.getUTCMonth(),
            this.customDateRange.startDate.getUTCDate(),
            0, 0, 0, 0
        ) : null;
        
        // If no dates selected, set start date (in UTC)
        if (!this.customDateRange.startDate) {
            this.customDateRange.startDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
            return;
        }
        
        // If only start date is set
        if (this.customDateRange.startDate && !this.customDateRange.endDate) {
            // If clicked date is before start, reset start to clicked date
            if (startTime && clickedTime < startTime) {
                this.customDateRange.startDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
            } else {
                // Set end date (in UTC, end of day)
                this.customDateRange.endDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
            }
            return;
        }
        
        // If both dates are set
        if (this.customDateRange.startDate && this.customDateRange.endDate) {
            // Check if startDate and endDate are the same day (single day selection)
            const startYear = this.customDateRange.startDate.getUTCFullYear();
            const startMonth = this.customDateRange.startDate.getUTCMonth();
            const startDay = this.customDateRange.startDate.getUTCDate();
            const endYear = this.customDateRange.endDate.getUTCFullYear();
            const endMonth = this.customDateRange.endDate.getUTCMonth();
            const endDay = this.customDateRange.endDate.getUTCDate();
            const isSameDay = startYear === endYear && startMonth === endMonth && startDay === endDay;
            
            // If it's a single day selection and clicked date is after start date, set as new end date
            if (isSameDay && startTime && clickedTime > startTime) {
                this.customDateRange.endDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
                return;
            }
            
            // Otherwise, reset the range
            this.customDateRange.startDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
            this.customDateRange.endDate = null;
        }
    }

    private isDateSelected(date: Date, type: "start" | "end"): boolean {
        if (type === "start" && this.customDateRange.startDate) {
            // Compare dates in UTC (year, month, day only)
            const dateUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
            const startUTC = Date.UTC(
                this.customDateRange.startDate.getUTCFullYear(),
                this.customDateRange.startDate.getUTCMonth(),
                this.customDateRange.startDate.getUTCDate()
            );
            return dateUTC === startUTC;
        }
        if (type === "end" && this.customDateRange.endDate) {
            // Compare dates in UTC (year, month, day only)
            const dateUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
            const endUTC = Date.UTC(
                this.customDateRange.endDate.getUTCFullYear(),
                this.customDateRange.endDate.getUTCMonth(),
                this.customDateRange.endDate.getUTCDate()
            );
            return dateUTC === endUTC;
        }
        return false;
    }

    private isDateInRange(date: Date): boolean {
        if (!this.customDateRange.startDate || !this.customDateRange.endDate) {
            return false;
        }
        // Compare dates in UTC
        const dateUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
        const startUTC = Date.UTC(
            this.customDateRange.startDate.getUTCFullYear(),
            this.customDateRange.startDate.getUTCMonth(),
            this.customDateRange.startDate.getUTCDate()
        );
        const endUTC = Date.UTC(
            this.customDateRange.endDate.getUTCFullYear(),
            this.customDateRange.endDate.getUTCMonth(),
            this.customDateRange.endDate.getUTCDate()
        );
        return dateUTC > startUTC && dateUTC < endUTC;
    }

    private get isCustomRangeValid(): boolean {
        // Valid if at least startDate is set (single day selection)
        return !!this.customDateRange.startDate;
    }

    private applyCustomDateRange() {
        if (!this.isCustomRangeValid) {
            return;
        }
        
        // If only startDate is set (single day), set endDate to the same day
        if (this.customDateRange.startDate && !this.customDateRange.endDate) {
            const startDate = this.customDateRange.startDate;
            // Set endDate to end of the same day (in UTC)
            this.customDateRange.endDate = new Date(Date.UTC(
                startDate.getUTCFullYear(),
                startDate.getUTCMonth(),
                startDate.getUTCDate(),
                23, 59, 59, 999
            ));
        }
        
        // Hide the date picker
        this.showCustomDatePicker = false;
        
        // Update the Custom option label with the selected range
        this.updateCustomOptionLabel();
        
        this.currentPage = 1;
        this.fetchData();
    }

    private getCustomDateRangeLabel(): string {
        if (!this.customDateRange.startDate) {
            return this.$t("account.history.custom") as string;
        }
        
        if (!this.customDateRange.endDate) {
            // Only start date selected - show just the day
            return this.formatDateForLabel(this.customDateRange.startDate);
        }
        
        // Check if it's a single day (compare UTC dates)
        const startYear = this.customDateRange.startDate.getUTCFullYear();
        const startMonth = this.customDateRange.startDate.getUTCMonth();
        const startDay = this.customDateRange.startDate.getUTCDate();
        const endYear = this.customDateRange.endDate.getUTCFullYear();
        const endMonth = this.customDateRange.endDate.getUTCMonth();
        const endDay = this.customDateRange.endDate.getUTCDate();
        const isSameDay = startYear === endYear && startMonth === endMonth && startDay === endDay;
        
        if (isSameDay) {
            // Single day - show just the day
            return this.formatDateForLabel(this.customDateRange.startDate);
        }
        
        // Date range - show both dates
        return `${this.formatDateForLabel(this.customDateRange.startDate)} - ${this.formatDateForLabel(this.customDateRange.endDate)}`;
    }

    private formatDateForLabel(date: Date): string {
        // Format as "DD. MMM YYYY" (e.g., "01. Feb 2026") for clarity
        // Use UTC methods since dates are stored in UTC
        const day = String(date.getUTCDate()).padStart(2, "0");
        const month = this.monthNames[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        return `${day}. ${month} ${year}`;
    }

    private updateCustomOptionLabel() {
        const newLabel = this.getCustomDateRangeLabel();
        
        // Only update selectedMonth label (this is what's displayed in the select)
        // Don't update monthOptions to avoid triggering change events
        if (this.selectedMonth && this.selectedMonth.value === "custom") {
            // Only update if label actually changed
            if (this.selectedMonth.label !== newLabel) {
                // Update the label directly - this might trigger a change event, but we handle it in onMonthChange
                this.selectedMonth.label = newLabel;
            }
        }
        
        // Update monthOptions for consistency (but this might trigger change event)
        const customOption = this.monthOptions.find((option: { value: string; label: string }) => option.value === "custom");
        if (customOption && customOption.label !== newLabel) {
            customOption.label = newLabel;
        }
    }

    private resetCustomOptionLabel() {
        const customLabel = this.$t("account.history.custom") as string;
        
        // Reset in monthOptions
        const customOption = this.monthOptions.find((option: { value: string; label: string }) => option.value === "custom");
        if (customOption) {
            customOption.label = customLabel;
        }
    }
}
